// ./src/components/features/Connectors/ConfigEditors/AirtableConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Text,
  Anchor,
  Alert,
  Code,
  Switch,
  Group,
  Box,
  List,
} from "@mantine/core";
import {
  IconInfoCircle,
  IconTable,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { AirtableConnectorConfig } from "@/types";

export interface AirtableConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: AirtableConnectorConfig = {
  accessToken: "",
  baseId: "",
  tableName: "",
  includeMetadata: true,
};

/**
 * Parse and validate Airtable connector configuration
 */
const parseAirtableConfig = (jsonString: string): AirtableConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

export const AirtableConnectorConfigEditor: React.FC<
  AirtableConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<AirtableConnectorConfig>(() =>
    parseAirtableConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseAirtableConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: AirtableConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle field changes
  const handleAccessTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, accessToken: e.target.value });
  };

  const handleBaseIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, baseId: e.target.value });
  };

  const handleTableNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, tableName: e.target.value });
  };

  const handleIncludeMetadataChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, includeMetadata: e.currentTarget.checked });
  };

  return (
    <Stack gap="md">
      {/* Setup Instructions */}
      <Alert icon={<IconTable size={16} />} color="yellow" variant="light">
        <Text size="sm" fw={500} mb="xs">
          Setup Instructions:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Go to{" "}
            <Anchor
              href="https://airtable.com/create/tokens"
              target="_blank"
              rel="noopener noreferrer"
            >
              airtable.com/create/tokens
            </Anchor>
          </List.Item>
          <List.Item>Click "Create new token"</List.Item>
          <List.Item>
            Add scopes: <Code>data.records:write</Code> and{" "}
            <Code>schema.bases:read</Code>
          </List.Item>
          <List.Item>Select the base(s) you want to use</List.Item>
          <List.Item>Copy the token</List.Item>
        </List>
      </Alert>

      {/* Access Token Field */}
      <TextInput
        label="Personal Access Token"
        placeholder="pat.xxxxxxxxxxxxxxxxxxxxxxxx"
        description="Token from Airtable"
        value={config.accessToken}
        onChange={handleAccessTokenChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
      />

      {/* Base ID Field */}
      <TextInput
        label="Base ID"
        placeholder="appXXXXXXXXXXXXXX"
        description="Starts with 'app'"
        value={config.baseId}
        onChange={handleBaseIdChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Help for Base ID */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Where to find the Base ID:
        </Text>
        <Text size="xs">Open your base. In the URL:</Text>
        <Text size="xs" mt={4}>
          <Code>
            https://airtable.com/<strong>appXXXXXXXXXXXXXX</strong>/...
          </Code>
        </Text>
      </Alert>

      {/* Table Name Field */}
      <TextInput
        label="Table Name"
        placeholder="Submissions"
        description="Name of the table (or table ID starting with 'tbl')"
        value={config.tableName}
        onChange={handleTableNameChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Toggle Options */}
      <Box>
        <Group justify="space-between">
          <div>
            <Text size="sm">Include Metadata</Text>
            <Text size="xs" c="dimmed">
              Add Timestamp, Submission ID, View URL fields
            </Text>
          </div>
          <Switch
            checked={config.includeMetadata !== false}
            onChange={handleIncludeMetadataChange}
            disabled={disabled}
          />
        </Group>
      </Box>

      {/* How It Works */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm" fw={500} mb="xs">
          How it works:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Form fields are matched to table columns by{" "}
            <strong>label first</strong>, then <strong>field name</strong>
          </List.Item>
          <List.Item>Each submission creates a new row in the table</List.Item>
          <List.Item>Hidden fields and security fields are excluded</List.Item>
        </List>
      </Alert>

      {/* Table Setup Tips */}
      <Alert
        icon={<IconAlertTriangle size={16} />}
        color="orange"
        variant="light"
      >
        <Text size="sm" fw={500} mb="xs">
          Table Setup Tips:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Create columns with <strong>same names</strong> as your form field
            labels or names
          </List.Item>
          <List.Item>
            For metadata, add columns: "Timestamp", "Submission ID", "View URL"
          </List.Item>
          <List.Item>Unknown fields are silently ignored (no error)</List.Item>
        </List>
      </Alert>
    </Stack>
  );
};
